import { Routes, Route, Navigate } from "react-router";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { PrivacyProvider } from "./lib/privacy";
import { AppLayout } from "./components/layout/app-layout";
import { LoginPage } from "./pages/login";
import { AuthCallbackPage } from "./pages/auth-callback";
import { BiometricDetail } from "./pages/biometrics/biometric-detail";
import { Dashboard } from "./pages/dashboard";
import { CategoryDetail } from "./pages/categories/category-detail";
import { PackagesPage } from "./pages/packages";
import { PackageDetail } from "./pages/packages/package-detail";
import { CatalogPage } from "./pages/catalog";
import { AnamnesisPage } from "./pages/anamnesis";
import { AnamnesisUserDetail } from "./pages/anamnesis/user-detail";
import { CrmPage } from "./pages/crm";
import { SystemPage } from "./pages/system";
import { MonitoringPage } from "./pages/monitoring";
import { AuthorizationPage } from "./pages/authorization";
import { DashboardsListPage } from "./pages/dashboards";
import { DashboardDetailPage } from "./pages/dashboards/dashboard-detail";
import { VariantDetailPage } from "./pages/assessment-variants/variant-detail";
import { CorrelationConfigsPage } from "./pages/correlation-configs";
import { CompaniesPage } from "./pages/companies";
import { HelfoEngineTestPage } from "./pages/helfo-engine-test";
import { FeatureFlagsPage } from "./pages/feature-flags";
import { SettingsPage } from "./pages/settings";
import "./index.css";

function AuthenticatedApp() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<Navigate to="/catalog" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="catalog" element={<CatalogPage />} />
        {/* Detail pages for catalog sub-items */}
        <Route path="biometrics/:id" element={<BiometricDetail />} />
        <Route path="categories/:id" element={<CategoryDetail />} />
        <Route path="packages" element={<PackagesPage />} />
        <Route path="packages/:id" element={<PackageDetail />} />
        <Route path="assessment-variants/:id" element={<VariantDetailPage />} />
        {/* Redirects for old list routes */}
        <Route path="biometrics" element={<Navigate to="/catalog?tab=biomarkorer" replace />} />
        <Route path="categories" element={<Navigate to="/catalog?tab=kategorier" replace />} />
        <Route path="labs" element={<Navigate to="/catalog?tab=labber" replace />} />
        <Route path="questions" element={<Navigate to="/catalog?tab=sporsmal" replace />} />
        <Route path="assessment-variants" element={<Navigate to="/catalog?tab=analysevarianter" replace />} />
        <Route path="requisition" element={<Navigate to="/anamnesis?tab=rekvirering" replace />} />
        {/* Main pages */}
        <Route path="anamnesis" element={<AnamnesisPage />} />
        <Route path="anamnesis/:userId" element={<AnamnesisUserDetail />} />
        <Route path="crm" element={<CrmPage />} />
        <Route path="authorization" element={<AuthorizationPage />} />
        <Route path="dashboards" element={<DashboardsListPage />} />
        <Route path="dashboards/:id" element={<DashboardDetailPage />} />
        <Route path="correlation-configs" element={<CorrelationConfigsPage />} />
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="system" element={<SystemPage />} />
        <Route path="monitoring" element={<MonitoringPage />} />
        <Route path="helfo-engine-test" element={<HelfoEngineTestPage />} />
        <Route path="feature-flags" element={<FeatureFlagsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
    </Routes>
  );
}

function AppRoutes() {
  const { isLoggedIn } = useAuth();

  if (!isLoggedIn) {
    return (
      <Routes>
        <Route path="auth/callback" element={<AuthCallbackPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return <AuthenticatedApp />;
}

export function App() {
  return (
    <AuthProvider>
      <PrivacyProvider>
      <AppRoutes />
      <Toaster
        theme="dark"
        toastOptions={{
          style: {
            background: "var(--color-admin-card)",
            border: "1px solid var(--color-admin-card-border)",
            color: "var(--color-admin-text-primary)",
          },
        }}
      />
      </PrivacyProvider>
    </AuthProvider>
  );
}

export default App;
